import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { fetchConsultations, updateConsultStatus, refundConsult, fetchRefundQueue } from "@admin/services/adminApi";
import { PageHeader, DataTable, Pagination, FilterBar, Pill } from "@admin/components/Primitives";
import { RefundQueueCard } from "@admin/components/RefundQueueCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LIMIT = 25;
const STATUSES = ["pending", "accepted", "confirmed", "live", "completed", "cancelled", "rejected", "refunded", "no_show", "lawyer_no_show"];
const STATUS_LABEL = { no_show: "No show — Client", lawyer_no_show: "No show — Lawyer" };
const labelOf = (s) => STATUS_LABEL[s] || s;
const PAY_STATUSES = ["paid", "pending", "refunded"];

const PILL_COLOR = {
  pending: "amber", accepted: "blue", confirmed: "blue", live: "emerald",
  completed: "emerald", cancelled: "rose", rejected: "rose", refunded: "rose", no_show: "rose", lawyer_no_show: "rose",
  paid: "emerald", "no-pay": "slate",
};

export default function AdminConsultations() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [paymentStatus, setPaymentStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [refundQueue, setRefundQueue] = useState(null);
  const [refundOnly, setRefundOnly] = useState(false);

  const loadQueue = useCallback(() => {
    fetchRefundQueue().then(setRefundQueue).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchConsultations({
        q: q || undefined, status, payment_status: paymentStatus, refund_pending: refundOnly ? "1" : undefined, skip, limit: LIMIT,
      });
      setData(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load consultations"); }
    setLoading(false);
  }, [q, status, paymentStatus, refundOnly, skip]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadQueue(); }, [loadQueue]);

  const showRefundQueue = () => { setSkip(0); setQ(""); setStatus("all"); setPaymentStatus("all"); setRefundOnly(true); };
  const refundQueueActive = refundOnly;

  const setStatusFor = async (cid, newStatus) => {
    try {
      await updateConsultStatus(cid, newStatus);
      toast.success(`Marked ${newStatus}`);
      await load();
      loadQueue();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const issueRefund = async (cid) => {
    if (!window.confirm("Mark this consultation as refunded? Razorpay refund must be processed separately for now.")) return;
    try {
      await refundConsult(cid, "Issued via admin panel");
      toast.success("Refund recorded");
      await load();
      loadQueue();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const columns = [
    {
      key: "id",
      label: "ID",
      render: (r) => <code className="text-[10px] text-slate-400">{(r.id || "").slice(0, 8)}</code>,
    },
    {
      key: "user_name",
      label: "Client",
      render: (r) => (
        <div className="min-w-[180px]">
          <p className="font-bold text-[#0A2540] text-sm">{r.user_name || "—"}</p>
          <p className="text-[11px] text-slate-500 truncate max-w-[200px]">{r.user_email || r.user_phone || ""}</p>
        </div>
      ),
    },
    {
      key: "lawyer_name",
      label: "Lawyer",
      render: (r) => <span className="text-sm">{r.lawyer_name}</span>,
    },
    {
      key: "consultation_type",
      label: "Type",
      render: (r) => <Pill color="blue">{r.consultation_type}</Pill>,
    },
    {
      key: "amount",
      label: "Amount",
      render: (r) => (
        <div>
          <p className="font-bold text-[#0A2540]">₹{r.amount}</p>
          {r.original_amount && r.original_amount !== r.amount && (
            <p className="text-[10px] text-slate-400 line-through">₹{r.original_amount}</p>
          )}
          {r.pricing_tier === "first" && <p className="text-[10px] font-bold text-[#D4AF37]">FIRST</p>}
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (r) => (
        <div className="flex flex-col gap-1">
          <Pill color={PILL_COLOR[r.status] || "slate"}>{labelOf(r.status || "pending")}</Pill>
          {r.payment_status === "paid" && <Pill color="emerald">Paid</Pill>}
          {r.payment_status === "refunded" && <Pill color="rose">Refunded</Pill>}
          {r.payment_status === "paid" && r.refund_status === "refund_pending" && <Pill color="amber">Refund pending</Pill>}
        </div>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (r) => (
        <div className="flex gap-1 flex-wrap min-w-[200px]">
          <Select value={r.status || "pending"} onValueChange={(v) => setStatusFor(r.id, v)}>
            <SelectTrigger className="w-[120px] h-7 text-xs" data-testid={`status-${r.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{labelOf(s)}</SelectItem>)}
            </SelectContent>
          </Select>
          {r.payment_status === "paid" && r.status !== "refunded" && (
            <button onClick={() => issueRefund(r.id)} className="px-2 py-1 text-[11px] font-bold rounded-md bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100" data-testid={`refund-${r.id}`}>
              Refund
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader testid="admin-consultations-header" title="Consultations" subtitle="View bookings, manage status, issue refunds." />
      <RefundQueueCard queue={refundQueue} active={refundQueueActive} onShow={showRefundQueue} />
      <FilterBar testid="consults-filterbar">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => { setSkip(0); setQ(e.target.value); }} placeholder="Search by user, lawyer, ID…" className="pl-8" data-testid="consults-search" />
        </div>
        <Select value={status} onValueChange={(v) => { setSkip(0); setRefundOnly(false); setStatus(v); }}>
          <SelectTrigger className="w-[150px]" data-testid="consults-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{labelOf(s)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={paymentStatus} onValueChange={(v) => { setSkip(0); setRefundOnly(false); setPaymentStatus(v); }}>
          <SelectTrigger className="w-[150px]" data-testid="consults-filter-payment"><SelectValue placeholder="Payment" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All payments</SelectItem>
            {PAY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </FilterBar>
      <DataTable testid="consults-table" columns={columns} rows={data.items} empty={loading ? "Loading…" : "No consultations"} />
      <Pagination testid="consults-pagination" total={data.total} skip={skip} limit={LIMIT} onChange={setSkip} />
    </>
  );
}
